// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
//
// Indexes the meetstandaard /monetarisering/onderbouwing document for lookup
// by MSI effect id, with a normalized-name fallback for entries whose
// effectId could not be matched at seed time. Every bron-id reference is
// resolved to its full entry from the document's central bronnen registry, so
// consumers never touch the registry themselves.

/**
 * @typedef {object} OnderbouwingBron
 * @property {string} id - Registry key (equals the id inside the entry)
 * @property {'universeel'|'literatuur'|string} type - Tarief/prijslijst vs wetenschappelijke bron (open enum)
 * @property {string} title - Source title
 * @property {string} [publisher] - Publishing organisation
 * @property {number} [year] - Publication year
 * @property {string|null} url - Public link; null when none exists (note explains why)
 * @property {string|null} [note] - Explanation shown when there is no url
 */

/**
 * @typedef {object} OnderbouwingOpbouwComponent
 * @property {string} component - Cost/benefit component (open enum, e.g. 'ziekteverzuim')
 * @property {string} stakeholder - Who bears the cost/benefit (open enum, e.g. 'werkgever')
 * @property {number} bedrag - € per person per year; all bedragen sum to the niveau's waardePerJaar
 * @property {string} berekening - Human-readable calculation
 * @property {Array<string>} aannames - Assumptions behind the calculation
 * @property {Array<OnderbouwingBron>} bronnen - Resolved sources
 */

/**
 * @typedef {object} OnderbouwingNiveau
 * @property {number} score - Score level (1..5)
 * @property {string} situatie - Description of the situation at this level
 * @property {number} waardePerJaar - € per person per year relative to the nullijn (negative = cost)
 * @property {string|null} literatuur - Prose justification from literature
 * @property {Array<OnderbouwingOpbouwComponent>|null} opbouw - Financial breakdown; null while still being authored
 * @property {Array<OnderbouwingBron>} bronnen - Resolved level-wide sources
 */

/**
 * @typedef {object} OnderbouwingEffect
 * @property {string} id - Stable slug (e.g. 'gezonde-leefstijl')
 * @property {string|null} effectId - MSI effect id (join key), null when unmatched
 * @property {string} effect - Display name
 * @property {string} categorie - Category name
 * @property {'volledig'|'gedeeltelijk'|'concept'|string} status - Completeness of the financial breakdown
 * @property {string} methodiek - Prose describing how the effect was monetised
 * @property {Array<{label: string, waarde: number, eenheid: string, bronnen: Array<OnderbouwingBron>}>} prijsbasis - Universal tariffs used
 * @property {Array<OnderbouwingNiveau>} niveaus - The five score levels
 */

/**
 * @typedef {object} OnderbouwingIndex
 * @property {Object<string, OnderbouwingEffect>} byEffectId - Lookup on the MSI effect id
 * @property {Object<string, OnderbouwingEffect>} byNormalizedName - Fallback lookup on the normalized effect name
 * @property {{version: string, source?: string, updatedAt?: string, status?: string, toelichting?: string}|null} meta - Document meta
 */

/**
 * Normalizes an effect name for the effectId-null fallback join: the API
 * contains names with trailing spaces and inconsistent casing.
 * @param {string|null|undefined} name - Raw effect name
 * @returns {string} normalized name ('' for missing input)
 */
export const normalizeEffectName = (name) =>
    typeof name === 'string' ? name.trim().toLowerCase().replace(/\s+/g, ' ') : ''

/**
 * Resolves bron ids against the registry, dropping ids that do not resolve
 * (the API guarantees they all do — this is defensive, never a crash).
 * @param {Array<string>|undefined} ids
 * @param {Object<string, OnderbouwingBron>} registry
 * @returns {Array<OnderbouwingBron>}
 */
const resolveBronnen = (ids, registry) =>
    Array.isArray(ids) ? ids.map((id) => registry[id]).filter(Boolean) : []

/**
 * Builds the lookup index for the onderbouwing document. Bron ids on niveaus,
 * opbouw components and prijsbasis are replaced by their resolved registry
 * entries. Effects with effectId null are only reachable via the name index.
 *
 * @param {object|null|undefined} doc - Raw /monetarisering/onderbouwing payload
 * @returns {OnderbouwingIndex}
 */
const buildOnderbouwingIndex = (doc) => {
    /** @type {OnderbouwingIndex} */
    const index = { byEffectId: {}, byNormalizedName: {}, meta: doc?.meta || null }
    const registry = doc?.bronnen || {}
    const effecten = Array.isArray(doc?.effecten) ? doc.effecten : []

    effecten.forEach((effect) => {
        const entry = {
            ...effect,
            prijsbasis: (Array.isArray(effect.prijsbasis) ? effect.prijsbasis : []).map((prijs) => ({
                ...prijs,
                bronnen: resolveBronnen(prijs.bronnen, registry),
            })),
            niveaus: (Array.isArray(effect.niveaus) ? effect.niveaus : []).map((niveau) => ({
                ...niveau,
                bronnen: resolveBronnen(niveau.bronnen, registry),
                opbouw: Array.isArray(niveau.opbouw)
                    ? niveau.opbouw.map((component) => ({
                        ...component,
                        aannames: Array.isArray(component.aannames) ? component.aannames : [],
                        bronnen: resolveBronnen(component.bronnen, registry),
                    }))
                    : null,
            })),
        }

        if (effect.effectId && !index.byEffectId[effect.effectId]) {
            index.byEffectId[effect.effectId] = entry
        }
        const nameKey = normalizeEffectName(effect.effect)
        if (nameKey && !index.byNormalizedName[nameKey]) {
            index.byNormalizedName[nameKey] = entry
        }
    })

    return index
}

/**
 * Looks up the onderbouwing entry for an app effect: primary join on the MSI
 * id, fallback on the normalized effect name (for document entries whose
 * effectId is null).
 *
 * @param {OnderbouwingIndex|null} index - Built index, or null while loading/failed
 * @param {string} msiId - The effect's MSIId (OutputEffects.MSIId)
 * @param {string} [name] - The effect's display name, for the fallback join
 * @returns {OnderbouwingEffect|null}
 */
export const findOnderbouwingEntry = (index, msiId, name) => {
    if (!index) return null
    return index.byEffectId[msiId] || index.byNormalizedName[normalizeEffectName(name)] || null
}

export default buildOnderbouwingIndex
